import * as ImagePicker from "expo-image-picker";
import { useState } from "react";
import {
    StyleSheet,
    View,
    TouchableOpacity,
    Image,
    ActivityIndicator,
    Platform
} from "react-native";
import { Text, Icon } from "react-native-paper";

import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors } from "@/lib/theme";
import { toJpeg } from "@/utils/resize-image";

interface SelfieStepProps {
    imageUri: string | null;
    onCapture: (uri: string) => void;
}

export function SelfieStep({ imageUri, onCapture }: SelfieStepProps) {
    const [capturing, setCapturing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function handleCapture() {
        setCapturing(true);
        setError(null);
        console.info(`[mobile ${MOBILE_DEBUG_VERSION}] selfie capture started`);

        try {
            if (Platform.OS === "web") {
                // Web PWA: expo's camera picker awaits a permission first, which
                // drops the user gesture so the browser suppresses the dialog.
                // Open a real file input (front-camera capture hint) instead.
                const uri = await pickWebSelfie();
                if (uri) {
                    onCapture(uri);
                }
                return;
            }
            const permission = await ImagePicker.requestCameraPermissionsAsync();
            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] selfie permission result`, permission);

            if (!permission.granted) {
                setError("Camera permission required");
                return;
            }

            const result = await ImagePicker.launchCameraAsync({
                mediaTypes: ["images"],
                quality: 0.7,
                allowsEditing: false,
                cameraType: ImagePicker.CameraType.front
            });

            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] selfie camera result`, {
                canceled: result.canceled,
                assets: result.canceled ? 0 : result.assets.length
            });

            if (result.canceled || result.assets.length === 0) {
                return;
            }
            // Defensive JPEG transcode (Rekognition rejects HEIC). The front
            // camera already emits JPEG, but this keeps the guarantee explicit.
            const jpegUri = await toJpeg(result.assets[0].uri);
            onCapture(jpegUri);
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : "Camera could not be opened. Please try again.";
            console.error(`[mobile ${MOBILE_DEBUG_VERSION}] selfie capture failed`, err);
            setError(message);
        } finally {
            setCapturing(false);
        }
    }

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Icon source="camera-account" size={32} color={colors.primary} />
                <Text style={styles.title}>Take a Selfie</Text>
                <Text style={styles.subtitle}>
                    Position your face clearly in the frame. Make sure you're in a well-lit area.
                </Text>
            </View>

            {imageUri ? (
                <View style={styles.previewContainer}>
                    {imageUri === "uploaded" ? (
                        <View
                            style={[
                                styles.preview,
                                { alignItems: "center", justifyContent: "center" }
                            ]}
                        >
                            <Icon source="check-circle" size={48} color={colors.success} />
                            <Text
                                style={{ color: colors.textSecondary, marginTop: 8, fontSize: 13 }}
                            >
                                Selfie uploaded
                            </Text>
                        </View>
                    ) : (
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: imageUri }}
                            style={styles.preview}
                        />
                    )}
                    <TouchableOpacity
                        style={styles.retakeButton}
                        onPress={handleCapture}
                        disabled={capturing}
                    >
                        <Icon source="camera-retake" size={18} color={colors.primary} />
                        <Text style={styles.retakeText}>Retake</Text>
                    </TouchableOpacity>
                </View>
            ) : (
                <TouchableOpacity
                    style={styles.captureButton}
                    onPress={handleCapture}
                    disabled={capturing}
                >
                    {capturing ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                        <>
                            <Icon source="camera" size={20} color="#FFFFFF" />
                            <Text style={styles.captureText}>Open Camera</Text>
                        </>
                    )}
                </TouchableOpacity>
            )}

            {error && <Text style={styles.errorText}>{error}</Text>}
        </View>
    );
}

/** Web-only selfie capture: a real <input type=file> with the front-camera hint. */
function pickWebSelfie(): Promise<string | null> {
    return new Promise((resolve) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        // Front camera on mobile web; plain file picker on desktop.
        input.setAttribute("capture", "user");
        input.style.display = "none";
        input.onchange = () => {
            const file = input.files?.[0];
            input.remove();
            resolve(file ? URL.createObjectURL(file) : null);
        };
        // iOS Safari only fires `change` for an <input> that is in the DOM, so
        // append it before opening the camera (otherwise the capture hangs).
        document.body.appendChild(input);
        input.click();
    });
}

const styles = StyleSheet.create({
    container: { flex: 1, gap: 20 },
    header: { alignItems: "center", gap: 8, paddingTop: 12 },
    title: { fontSize: 20, fontWeight: "700", color: colors.textPrimary },
    subtitle: {
        fontSize: 14,
        color: colors.textSecondary,
        textAlign: "center",
        paddingHorizontal: 24
    },
    previewContainer: { alignItems: "center", gap: 12 },
    preview: {
        width: 220,
        height: 280,
        borderRadius: 16,
        backgroundColor: colors.backgroundSecondary
    },
    retakeButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.primary
    },
    retakeText: { fontSize: 14, fontWeight: "600", color: colors.primary },
    captureButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 12,
        paddingVertical: 48,
        marginHorizontal: 24
    },
    captureText: { fontSize: 16, fontWeight: "700", color: "#FFFFFF" },
    errorText: { fontSize: 12, color: colors.error, textAlign: "center" }
});
